import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'components/Table';
import { DOCUMENT_STATUS } from 'components/CarrierDetails/Constants';
import { Tag, Button, Tooltip } from '@ontruck/orc';
import config from 'config';
import { Select } from '../../modules/filters/utils';
import {
  getCarrierProfiles as fetchProfiles,
  postDeleteDocument,
  putDocumentExpirationDate,
} from './http';
import CopyButton from 'components/CopyButton';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';

import ReasonDeleteModal from 'components/ReasonDeleteModal';
import { Loading } from 'components/Loading';
import DocumentsUpload from 'components/DocumentsUpload';
import InlineDateUpdate from 'components/InlineDateUpdate';

const t = {
  filename: 'Filename',
  type: 'Type',
  expiration_date: 'Expiration date',
  status: 'Status',
  other: 'Other',
  title: 'Documents',
  carrier_profiles: 'Carrier profiles',
  actions: 'Actions',
  upload_link: 'Upload',
  status_new: 'NEW',
  status_validated: 'VALIDATED',
  status_invalid: 'INVALID',
  status_expired: 'EXPIRED',
  status_not_uploaded: 'NOT UPLOADED',
  status_close_to_expire: 'CLOSE TO EXPIRATION',
  status_removed: 'REMOVED',
  noDocs: 'There are no documents at this time.',
  profilelabel: 'Carrier document profiles: ',
  landingLinkButtonLabel: 'Copy Landing link',
  copied: 'Copied!',
  deleteModal: {
    title: 'Please, enter a reason to delete',
    text: 'You will remove the document.',
    fieldText: 'Deleting reason',
    cancel: 'Cancel',
    remove: 'Remove',
  },
  action_upload: 'Upload',
  action_download: 'Download',
  action_see: 'See',
  action_delete: 'Delete',
};

const STATUS_DATA = {
  [DOCUMENT_STATUS.status_new]: {
    text: t.status_new,
    skin: 'modernLightGrey',
  },
  [DOCUMENT_STATUS.status_validated]: {
    text: t.status_validated,
    skin: 'modernGrey',
  },
  [DOCUMENT_STATUS.status_invalid]: {
    text: t.status_invalid,
    skin: 'modernRed',
  },
  [DOCUMENT_STATUS.status_expired]: {
    text: t.status_expired,
    skin: 'modernRed',
  },
  [DOCUMENT_STATUS.status_close_to_expire]: {
    text: t.status_close_to_expire,
    skin: 'modernOrange',
  },
  [DOCUMENT_STATUS.status_removed]: {
    text: t.status_removed,
    skin: 'modernRed',
  },
  status_not_uploaded: {
    text: t.status_not_uploaded,
    skin: '',
  },
};

const formatCarrierProfiles = (carrierProfiles) => {
  return carrierProfiles.map((profile) => {
    return { label: profile.profile_name, value: profile.id };
  });
};

const isRemovable = (document) =>
  [DOCUMENT_STATUS.status_new, DOCUMENT_STATUS.status_validated].includes(document.status);

const isDownloadable = (document) =>
  [
    DOCUMENT_STATUS.status_new,
    DOCUMENT_STATUS.status_validated,
    DOCUMENT_STATUS.status_invalid,
    DOCUMENT_STATUS.status_close_to_expire,
    DOCUMENT_STATUS.status_expired,
  ].includes(document.status);

const isNew = (document) => [DOCUMENT_STATUS.status_new].includes(document.status);

const DocumentExpirationDate = ({ document }) => {
  const [currentExpirationDate, setCurrentExpirationDate] = useState(document.expiration_date);
  const handleDateUpdate = useCallback(
    async (date) => {
      try {
        const updatedMetadata = await putDocumentExpirationDate(document.id, date);
        setCurrentExpirationDate(updatedMetadata.metadata.expiration_date);
      } catch (err) {
        console.error('Error putDocumentExpirationDate date', err);
      }
    },
    [document]
  );
  if (!document.id) return <span></span>;

  return <InlineDateUpdate currentDate={currentExpirationDate} handleSave={handleDateUpdate} />;
};

export const DocumentsList = ({
  serverDocuments,
  className,
  carrierProfiles,
  showProfileSelector,
  onChangeProfile,
  landingLink,
  entity,
  refreshDocuments,
}) => {
  const [profilesOptions, setProfilesOptions] = useState(null);
  const [selectedProfiles, setSelectedProfiles] = useState(formatCarrierProfiles(carrierProfiles));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [toDeleteDocument, setToDeleteDocument] = useState(null);
  const [documents, setDocuments] = useState(serverDocuments);

  useEffect(() => {
    setDocuments(serverDocuments);
  }, [serverDocuments]);

  useEffect(() => {
    const getCarrierProfiles = async () => {
      try {
        const profiles = await fetchProfiles();
        const options = (profiles || []).map((profile) => {
          return { label: profile.profile_name, value: profile.id };
        });
        setProfilesOptions(options);
      } catch (err) {
        console.error('Error fetching profiles options', err);
      }
    };
    if (!profilesOptions && showProfileSelector) {
      getCarrierProfiles();
    }
  }, [profilesOptions, showProfileSelector]);

  const handleProfileChange = useCallback((selectedOptions) => {
    setSelectedProfiles(selectedOptions ? selectedOptions : []);
  }, []);

  const handleOnBlur = useCallback(() => {
    const formatProfiles = selectedProfiles.map((profile) => profile.value);
    onChangeProfile(formatProfiles);
  }, [selectedProfiles, onChangeProfile]);

  const handleDownload = useCallback(({ status, doc_uuid }) => {
    if (
      ['status_validated', 'status_invalid', 'status_close_to_expire', 'status_expired'].indexOf(
        status
      ) > -1 &&
      doc_uuid
    ) {
      window.open(`${config.BASE_URL}/api/dms/download/${doc_uuid}/`);
    }
  }, []);

  const handleNavigate = useCallback(({ id }) => {
    if (id) {
      window.open(`${config.BASE_ROUTE}docs/${id}`);
    }
  }, []);

  const renderHeaders = () => {
    return (
      <TableHeader className="documents-list__header">
        <TableRow className="documents-list__header-row">
          <TableCell>{t.type}</TableCell>
          <TableCell>{t.expiration_date}</TableCell>
          <TableCell>{t.status}</TableCell>
          <TableCell>{t.actions}</TableCell>
        </TableRow>
      </TableHeader>
    );
  };

  const renderStatusTag = (document) => {
    if (STATUS_DATA[document.status]) {
      return (
        <Tag
          text={STATUS_DATA[document.status].text}
          skin={STATUS_DATA[document.status].skin}
          weight="bold"
          size="M"
        />
      );
    } else {
      return <Tag text={t.other} skin="modernGrey" weight="bold" size="M" />;
    }
  };

  const handleShowDeleteModal = useCallback((e, document) => {
    e.stopPropagation();
    setShowDeleteModal(true);
    setToDeleteDocument(document);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setToDeleteDocument(null);
  }, []);

  const updateDeletedDocument = useCallback(
    (updatedDocument) => {
      setDocuments(
        documents.map((item) => (item.id === updatedDocument.id ? updatedDocument : item))
      );
    },
    [documents]
  );

  const handleDeleteDocument = useCallback(
    async (comment) => {
      try {
        setLoadingDocId(toDeleteDocument.id);
        const updatedDocument = await postDeleteDocument(toDeleteDocument.id, {
          comment,
          document_type_name: toDeleteDocument.document_type.label,
          document_type: toDeleteDocument.document_type.id,
          entity_type: entity.entity_type,
          entity_id: entity.entity_id,
        });
        updateDeletedDocument(updatedDocument);
        refreshDocuments();
      } catch (err) {
        console.error('Error postDeleteDocument', err);
      } finally {
        setToDeleteDocument(null);
        setLoadingDocId(null);
      }
    },
    [toDeleteDocument, entity, refreshDocuments, updateDeletedDocument]
  );

  const renderTable = () => {
    if (documents.length === 0) {
      return (
        <h5 className="documents-list__no-docs" data-testid="documents-list__no-docs">
          {t.noDocs}
        </h5>
      );
    } else {
      return (
        <Table className="documents-list__table">
          {renderHeaders()}
          <TableBody>
            {documents.map((document) => {
              const isLoading = loadingDocId === document.id;
              return (
                <TableRow
                  key={document.id}
                  className="documents-list__row"
                  dataTestId="documentsListRow"
                >
                  <TableCell className="documents-list__cell-type">
                    {document.document_type?.label}
                  </TableCell>
                  <TableCell>
                    {document.expiration_date && (
                      <div data-testid="documents-list__cell-expiration-date">
                        <DocumentExpirationDate document={document} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="documents-list__cell-status">
                    {renderStatusTag(document)}
                  </TableCell>
                  <TableCell>
                    <div className="documents-list__cell-actions">
                      {isLoading && (
                        <div className="documents-list__cell-actions__loading">
                          <Loading />
                        </div>
                      )}
                      {!isLoading && (
                        <Tooltip component={t.action_upload} position="bottom" skin="light">
                          <DocumentsUpload
                            className="documents-list__icon"
                            entity_uuid={entity.entity_uuid}
                            entity_type={entity.entity_type}
                            document={document}
                            refetchDocuments={refreshDocuments}
                          />
                        </Tooltip>
                      )}
                      {isDownloadable(document) && !isLoading && (
                        <Tooltip component={t.action_download} position="bottom" skin="light">
                          <Button
                            className="documents-list__icon-button"
                            nude
                            skin="primary"
                            data-testid="documents-list__download-button"
                            onClick={() => handleDownload(document)}
                          >
                            <DownloadIcon className="documents-list__icon" />
                          </Button>
                        </Tooltip>
                      )}
                      {isNew(document) && !isLoading && (
                        <Tooltip component={t.action_see} position="bottom" skin="light">
                          <Button
                            className="documents-list__icon-button"
                            nude
                            skin="primary"
                            data-testid="documents-list__eye-button"
                            onClick={() => handleNavigate(document)}
                          >
                            <EyeIcon className="documents-list__icon" />
                          </Button>
                        </Tooltip>
                      )}
                      {isRemovable(document) && !isLoading && (
                        <Tooltip component={t.action_delete} position="bottom" skin="light">
                          <Button
                            className="documents-list__icon-button"
                            nude
                            skin="primary"
                            data-testid="documents-list__delete-button"
                            onClick={(e) => handleShowDeleteModal(e, document)}
                          >
                            <TrashIcon className="documents-list__icon" />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      );
    }
  };

  return (
    <div className={className} data-testid="documentsList">
      <div className="documents-list">
        <h2 className="documents-list__title">{t.title}</h2>
        <div className="documents-list__title-row">
          {showProfileSelector && (
            <div className="documents-list__profile-container">
              <label
                className="documents-list__profile-label"
                data-testid="documents-list__profile-selector"
                htmlFor="documents-list__profile-selector"
              >
                {t.profilelabel}
              </label>
              <Select
                id="documents-list__profile-selector"
                className="documents-list__profile-selector"
                closeMenuOnSelect={false}
                value={selectedProfiles}
                options={profilesOptions || []}
                isMulti
                placeholder={t.carrier_profiles}
                onChange={handleProfileChange}
                onBlur={handleOnBlur}
              />
            </div>
          )}
          <CopyButton
            getText={() => Promise.resolve(landingLink)}
            feedbackSuccess={t.copied}
            small
            className="documents-list__copy-landing"
            data-testid="documents-list__copy-landing-button"
          >
            {t.landingLinkButtonLabel}
          </CopyButton>
        </div>
        {renderTable()}
      </div>
      <ReasonDeleteModal
        content={t.deleteModal}
        show={showDeleteModal}
        onClose={closeDeleteModal}
        disabled={loadingDocId}
        onSave={handleDeleteDocument}
      />
    </div>
  );
};

DocumentsList.defaultProps = {
  serverDocuments: [],
  carrierProfiles: [],
  showProfileSelector: false,
};

DocumentsList.propTypes = {
  serverDocuments: PropTypes.array,
  className: PropTypes.string,
  carrierProfiles: PropTypes.array,
  showProfileSelector: PropTypes.bool,
  onChangeProfile: PropTypes.func,
  landingLink: PropTypes.string,
  entity: PropTypes.object.isRequired,
  refreshDocuments: PropTypes.func,
};

export default DocumentsList;
